import React from 'react';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import { Typography, Button, FormControl, FormControlLabel, Dialog, DialogContent, DialogActions, Toolbar } from '@mui/material';
import Checkbox from '../../components/checkbox';
import Auth from '../../auth';
import axios from 'axios';
import Config from '../../config';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// reports.tsx/scorecards.tsx/user_profile.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;

type AgreementRecord = {
    Name?: string;
};

type AgreementsProps = {
    loadAgreements: (updateState?: boolean) => void;
    inNetwork?: boolean;
    isBillable?: boolean;
    isRetailer?: boolean;
    agreements?: AgreementRecord[];
    companyName?: string;
};

// A function component that also carries the agreement's static metadata
// (name/title) and an `isActive` predicate. `getActiveAgreement` reads those
// statics off the component to decide which agreement (if any) to prompt for,
// and renders the component directly via <Agreement {...props}/>. This mirrors
// the previous class shape (agreementName/agreementTitle fields + isActive
// method) without needing `new` to reach the metadata.
type AgreementComponent = React.FC<AgreementsProps> & {
    agreementName: string;
    agreementTitle: string;
    isActive: (props: AgreementsProps) => any;
};

const CCNAgreement = ((props: AgreementsProps) => {
    return <>
        <Typography>I, as authorized representative of {props.companyName}, register to be a part of Logicbroker’s Connected Commerce Network. I agree the Company, its products and services, and contact information made available to Logicbroker may be displayed to and viewed by other members of the Connected Commerce Network. In addition, as part of the Connected Commerce Network, I agree to be named in Logicbroker’s newsletters, website, and additional published articles as a trusted supplier and/or retailer and member of the Connected Commerce Network. </Typography>
        <FormControl style={{marginTop: '1em'}}>
            <FormControlLabel label='By checking this box, I accept the Terms of Use governing the Connected Commerce Network' control={<Field name='tos' validate={(v: any) => !v} type="checkbox" component={Checkbox}/>}/>
        </FormControl>
        <FormControl style={{marginTop: '1em'}}>
            <FormControlLabel label='By checking this box, I accept the Privacy Policy governing the Connected Commerce Network' control={<Field name='privacy' validate={(v: any) => !v} type="checkbox" component={Checkbox}/>}/>
        </FormControl>
    </>;
}) as AgreementComponent;
CCNAgreement.agreementName = "CCN";
CCNAgreement.agreementTitle = "Connected Commerce Network Addendum";
CCNAgreement.isActive = (props: AgreementsProps) =>
    Auth.hasPermission("ccn/manage") && props.inNetwork && !props.isRetailer && props.agreements.filter(r => r.Name === 'CCN').length === 0;

// Registry of agreement components. Each carries static agreementName/
// agreementTitle/isActive metadata (see CCNAgreement); getActiveAgreement
// loops this list and returns the first whose isActive(props) is truthy.
const agreements: AgreementComponent[] = [CCNAgreement];

const getActiveAgreement = (props: AgreementsProps): any => {
    if(!props.agreements){
        return undefined;
    }
    for(let i = 0; i < agreements.length; i++){
        if(agreements[i].isActive(props)){
            return agreements[i];
        }
    }
}

const Agreements = (props: AgreementsProps) => {
    const handleSubmit = async (name: string) => {
        await axios.post(Config.oauth + '/user/acceptagreement?agreementName=' + name);
        props.loadAgreements(true);
    };

    let Agreement = getActiveAgreement(props);
    let open = !(!getActiveAgreement(props)) && !Auth.getEmail().endsWith("@logicbroker.com") && !Auth.getEmail().endsWith("@logictec.com");
    if(!Agreement){
        return '';
    }
    return <Form onSubmit={() => handleSubmit(Agreement.agreementName)}
    render={({ handleSubmit, pristine, invalid, values, submitting }: any) => (
      <form onSubmit={handleSubmit}>
      <Dialog open={open}>
        <Toolbar className='lbtoolbar'>User Agreement: {Agreement.agreementTitle}</Toolbar>
        <DialogContent>
            <Agreement {...props}/>
        </DialogContent>
        <DialogActions>
            <Button disabled={submitting || invalid} color='primary' onClick={handleSubmit}>Accept and continue</Button>
        </DialogActions>
      </Dialog>
    </form>)}
    />;
};

export default Agreements;
